/**
 * Rolled-up quantities on screen: one line per tree row, one table per pane.
 *
 * Two renderings of the same reading, because the two places have different room
 * and the same obligation. Every word either shows comes from `rollup-model.ts`,
 * which is pure and tested directly - nothing here decides when a `≥` appears or
 * how a partial total is worded, because that is where a plausible-looking wrong
 * answer hides and a render test cannot see it.
 *
 * What both renderings refuse to do:
 *
 *   - print a bare figure for a floor. The marker is inside the text the model
 *     returns, so there is no way to render the number and drop the qualifier;
 *   - print 0 for an absent total. "not stated" is a different answer and the
 *     nullable columns exist to keep it one;
 *   - swallow `problems`. A value in the wrong unit, or one nobody could parse,
 *     is REFUSED by the server rather than guessed at, and hiding the refusal
 *     turns an honest gap into a silent wrong number. The tree says how many;
 *     the pane says what, in the server's own words.
 *
 * Columns are handed in. They come from `attributes` on the payload, never from a
 * list written here - see `rollupColumns`.
 *
 * Nothing here writes, so nothing here needs a permission: the totals are
 * computed from the model rather than stored, and the pane says so instead of
 * offering a control that would have nothing to save.
 */

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import HelpTip from '../common/HelpTip'
import type { PartTotal } from '../../schema'
import { MONOSPACE } from '../../theme'
import {
  NO_ROLLUP_NOTE,
  readOwn,
  readTotal,
  rollupProblems,
  unstorableNote,
} from './rollup-model'
import type { RollupColumn } from './rollup-model'

export const ROLLUP_TABLE_LABEL = 'Rolled-up quantities'

export const DERIVED_NOTE =
  'A total is computed from the model rather than stored, so there is nothing to ' +
  'edit in that column: it changes by changing what the parts state — which is what ' +
  'the quantities below are.'

export const PROBLEMS_TITLE = 'Left out of these totals'

/**
 * One tree row's totals, short enough to sit under a name.
 *
 * The figure carries its own `≥`; "partial" names the parts responsible up to
 * three of them, and the tooltip carries the whole sentence. A row that simply
 * showed the number would be the failure this shape exists to prevent.
 */
export function TotalsSummary({
  totals,
  isDefinition,
  columns,
}: {
  totals: Record<string, PartTotal> | null | undefined
  isDefinition: boolean
  columns: readonly RollupColumn[]
}) {
  if (columns.length === 0) return null

  return (
    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', minWidth: 0 }}>
      {columns.map((column) => {
        const total = totals?.[column.name]
        const reading = readTotal(total, column)
        const own = readOwn(total, column, isDefinition)
        return (
          <Tooltip key={column.name} title={reading.detail} describeChild>
            <Typography
              variant="caption"
              component="span"
              color={reading.known ? 'text.primary' : 'text.secondary'}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {column.label}{' '}
              </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {reading.text}
              </Box>
              {/* Own only when there IS one: every row of /api/design is a usage,
                  and a usage states nothing of its own, so a column of dashes
                  would be noise where the tooltip already explains. */}
              {own.counted && <Box component="span">{` (own ${own.text})`}</Box>}
              {reading.partialShort && (
                <Box component="span" sx={{ color: 'warning.main' }}>
                  {` · ${reading.partialShort}`}
                </Box>
              )}
              {reading.problemsShort && (
                <Box component="span" sx={{ color: 'error.main' }}>
                  {` · ${reading.problemsShort}`}
                </Box>
              )}
            </Typography>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

/**
 * One part's own value beside the total of its subtree, per rolling quantity.
 *
 * Both numbers, because the contract's distinction between what a part weighs
 * itself and what everything inside it weighs is the point of the feature - and a
 * total on its own cannot be checked by the person reading it.
 */
export function TotalsTable({
  totals,
  stated,
  isDefinition,
  columns,
  unstorable,
}: {
  totals: Record<string, PartTotal> | null | undefined
  /** The part's stored values by column, so a REFUSED one is not read as absent. */
  stated: Record<string, unknown>
  isDefinition: boolean
  columns: readonly RollupColumn[]
  unstorable: readonly string[]
}) {
  const problems = rollupProblems(totals, columns)
  const missing = unstorableNote(unstorable)

  if (columns.length === 0) {
    return (
      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60ch' }}>
          {NO_ROLLUP_NOTE}
        </Typography>
        {missing && <Alert severity="info">{missing}</Alert>}
      </Stack>
    )
  }

  return (
    <Stack spacing={1}>
      <Table size="small" aria-label={ROLLUP_TABLE_LABEL}>
        <TableHead>
          <TableRow>
            {/* ATTRIBUTE, not Quantity. The sections above are Part
                Attributes and Rollup Attributes, and the column naming
                the same thing a third way made them read as three
                different things. Several of these are not quantities
                anyway - a subsystem is an enumeration. */}
            <TableCell>Attribute</TableCell>
            <TableCell>Own</TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Rollup
                {/* The one thing about this column that is not obvious, and the
                    only thing worth a sentence: the total INCLUDES the part's
                    own value, so 'Own 40, Rollup 141.85' is not 40 plus 141.85.
                    It is obvious the moment either changes; it is not obvious
                    the first time somebody reads two numbers side by side. */}
                <HelpTip
                  label="What the rollup includes"
                  title={
                    'This part\u2019s own value plus the total of every part inside it, times ' +
                    'how many of each \u2014 so it already contains the Own column rather than ' +
                    'adding to it. \u2265 means something below was counted short and the real ' +
                    'figure can only be higher; \u2264 means the opposite, on an attribute that ' +
                    'aggregates as its weakest part. Hover a figure for what is missing.'
                  }
                />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {columns.map((column) => {
            const total = totals?.[column.name]
            const reading = readTotal(total, column)
            const own = readOwn(total, column, isDefinition, stated[column.name])
            return (
              <TableRow key={column.name}>
                <TableCell>
                  {column.label}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontFamily: MONOSPACE }}
                  >
                    {column.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {own.note ? (
                    <Tooltip title={own.note} describeChild>
                      <Typography variant="body2" color="text.secondary">
                        {own.text}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2">{own.text}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={reading.detail} describeChild>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      color={reading.known ? 'text.primary' : 'text.secondary'}
                    >
                      {reading.text}
                    </Typography>
                  </Tooltip>
                  {/* THE SHORT FORMS. 'partial: FSW' rather than a sentence
                      naming the parts and explaining what partial means, on
                      every row of every part. The full wording is already in
                      `detail`, which is the tooltip on the figure above - so
                      nothing is lost, it is one hover away instead of always
                      on screen.

                      The bound note is gone from the cell entirely: '≥' is
                      already in the figure, and the sentence explaining which
                      way it is wrong is in the same tooltip. */}
                  {reading.partialShort && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                      {reading.partialShort}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {/* DERIVED_NOTE printed here, under every part's table. It says the
          figures are computed rather than stored, which the column heading and
          its '?' now carry between them. */}

      {/* The server's own words, verbatim: it names the part and says why the
          value was not used, which no paraphrase here would improve on. */}
      {problems.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>{PROBLEMS_TITLE}</AlertTitle>
          <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
            {problems.map((problem) => (
              <li key={problem}>
                <Typography variant="body2" component="span">
                  {problem}
                </Typography>
              </li>
            ))}
          </Stack>
        </Alert>
      )}

      {missing && <Alert severity="info">{missing}</Alert>}
    </Stack>
  )
}
